import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';

// Custom Hooks
import { useCourseData } from '../../hooks/useCourseData';

// Components
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { CourseContentTabs } from '../../components/courses/CourseContentTabs';
import { CourseHeader } from '../../components/courses/CourseHeader';
import { CourseProgress } from '../../components/courses/CourseProgress';
import { ModuleList } from '../../components/courses/ModuleList';
import { SubjectSelector } from '../../components/courses/SubjectSelector';
import { VideoPlayer } from '../../components/courses/VideoPlayer';
import { app } from "../../firebase"; // Ensure your firebase app is correctly initialized  



export const CourseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Module state
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [selectedModuleId, setSelectedModuleId] = useState('');

  // Video dialog state
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<any>(null);

  // Course data hook
  const {
    isLoading,
    courseData,
    modules,
    subjects,
    selectedSubject,
    batches,
    handleSubjectClick,
    handleDownload,
    handleSyllabusDownload1,
    handleAllMaterialsDownload
  } = useCourseData(id || '');
  const [subjectsData, setSubjectsData] = useState([]);
  // Firebase Firestore database  
  const db = getFirestore(app);
  const [user, setUserData] = useState(null);

  // Set first module as selected by default when modules change
  useEffect(() => {
    const { emailId } = JSON.parse(localStorage.getItem('user') || 'null') ?? {}; // Update with your actual key  

    const fetchUserData = async () => {
      try {
        const userQuery = query(collection(db, "students"), where("emailId", "==", emailId));
        const querySnapshot = await getDocs(userQuery);

        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          setUserData(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch user data.",
        });
      }
    }

    if (modules.length > 0) {
      setSelectedModuleId(modules[0].id);
      setExpandedModules({ [modules[0].id]: true });
    }

    fetchUserData();
  }, [modules]);

  // Toggle module expansion
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Handle module selection
  const handleModuleSelection = (moduleId: string) => {
    setSelectedModuleId(moduleId);
    // Expand the selected module
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: true
    }));

    // Scroll to the module
    const moduleElement = document.getElementById(moduleId);
    if (moduleElement) {
      moduleElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle video playback
  const handleWatchVideo = (video: any) => {
    if (!video) {
      toast({
        title: "Video Error",
        description: "Video URL is missing or invalid.",
        variant: "destructive",
      });
      return;
    }

    setCurrentVideo(video);
    setVideoDialogOpen(true);
  };

  // Handle start assessment
  const handleStartAssessment = () => {
    navigate(`/assessments/take/${id}`);
  };

  // Continue learning handler
  const handleContinueLearning = () => {
    // Implement continue learning logic
    toast({
      title: "Continue Learning",
      description: "Resuming your course from where you left off.",
    });
  };

  // Calculate total and completed lessons
  const calculateLessons = () => {
    let total = 0;
    let completed = 0;

    modules.forEach(module => {
      if (module.batches && module.batches.length) {
        total += module.batches.length;
        completed += module.batches.filter((lesson: any) => lesson.isCompleted).length;
      }
    });

    return { total, completed };
  };

  const { total: totalLessons, completed: completedLessons } = calculateLessons();

  // Loading state
  if (isLoading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-4xl">
          <div className="h-64 bg-gray-200 rounded-xl"></div>
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (!courseData) {
    return <div>Course not found</div>;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-3/3">
          <CourseHeader
            course={courseData}
            onContinueLearning={handleContinueLearning}
          />

          {/* Subject selection */}
          <SubjectSelector
            subjects={subjects}
            selectedSubject={selectedSubject}
            onSubjectClick={handleSubjectClick}
          />
        </div>

        <CourseProgress
          completedLessons={completedLessons}
          totalLessons={totalLessons}
          duration={courseData?.duration || "120 hours"}
          modulesCount={modules?.length || 0}
          onSyllabusDownload={handleSyllabusDownload1}
          onAllMaterialsDownload={handleAllMaterialsDownload}
          onStartAssessment={handleStartAssessment}
        />
      </div>

      {/* Module List */}
      <ModuleList
        modules={modules}
        selectedModuleId={selectedModuleId}
        expandedModules={expandedModules}
        onModuleSelect={handleModuleSelection}
        onToggleModule={toggleModule}
        onDownload={handleDownload}
        onWatchVideo={handleWatchVideo}
        batchIds={user?.batchIds ?? batches}
      />

      {/* Course Content Tabs */}
      <CourseContentTabs
        modules={modules}
        course={courseData}
        onWatchVideo={handleWatchVideo}
      />

      {/* Video Dialog */}
      <VideoPlayer
        open={videoDialogOpen}
        onOpenChange={setVideoDialogOpen}
        video={currentVideo}
      />
    </div>
  );
};